import { afterNextRender, Component, HostListener, inject, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService, PageHeaderService } from '@core/services';
import { pageUrls } from './configuration/pages';
import { LoginComponent } from '@modules/auth/login/login.component';
import { PasswordForgetComponent } from '@modules/auth/password-forget/password-forget.component';
import { PasswordResetComponent } from '@modules/auth/password-reset/password-reset.component';
import { BaseHeaderComponent } from '../app/components/base-header/base-header.component';
import { SidebarComponent } from '../app/components/sidebar/sidebar.component';
import { DrawerHostComponent } from '@shared/components/drawer-host/drawer-host.component';
import { DialogHostComponent } from '@shared/components/dialog-host/dialog-host.component';
import { ModalHostComponent } from '@shared/components/modal-host/modal-host.component';
import { EntryComponent } from '@components/entry/entry.component';
import { animate, group, query, style, transition, trigger } from '@angular/animations';

export const routeAnimations = trigger('routeAnimations', [
  transition('* <=> *', [
    query(':enter, :leave', [style({ position: 'absolute', width: '100%', height: '100%' })], { optional: true }),
    group([
      query(':leave', [animate('180ms ease-in', style({ opacity: 0, transform: 'translateY(6px)' }))], {
        optional: true,
      }),
      query(
        ':enter',
        [
          style({ opacity: 0, transform: 'translateY(-6px)' }),
          animate('220ms 100ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
        ],
        { optional: true },
      ),
    ]),
  ]),
]);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeAnimations],
  imports: [
    LoginComponent,
    PasswordForgetComponent,
    PasswordResetComponent,
    BaseHeaderComponent,
    SidebarComponent,
    RouterOutlet,
    DrawerHostComponent,
    DialogHostComponent,
    ModalHostComponent,
    EntryComponent,
  ],
})
export class AppComponent {
  readonly loader = signal(true);
  readonly isLoginPage = signal(false);
  readonly isPasswordForgotPage = signal(false);
  readonly isPasswordResetPage = signal(false);
  protected readonly isSideNavOpen = signal(false);

  readonly header = viewChild<BaseHeaderComponent>('header');
  readonly sideNav = viewChild<SidebarComponent>('sideNav');

  private readonly desktopBreakpoint = 1024;
  private autoCollapsedByResize = false;
  private firstNavigationDone = false;
  private viewReady = false;

  public authService = inject(AuthService);
  readonly pageHeader = inject(PageHeaderService);
  private router = inject(Router);

  constructor() {
    // Track every NavigationEnd — first one means the initial route + lazy chunks resolved
    this.router.events.subscribe((val) => {
      if (!(val instanceof NavigationEnd)) return;

      this.isLoginPage.set(this.router.url.includes(pageUrls.login));
      this.isPasswordForgotPage.set(this.router.url.includes(pageUrls.passwordForgot));
      this.isPasswordResetPage.set(this.router.url.includes(pageUrls.passwordReset));

      if (!this.firstNavigationDone) {
        this.firstNavigationDone = true;
        this.tryUnlock();
      }
    });

    // afterNextRender fires once the DOM is painted — Angular web components are registered
    afterNextRender(() => {
      this.viewReady = true;
      this.tryUnlock();
      this.connectHeaderSideNav();
      this.applyResponsiveLayout();
    });
  }

  /**
   * Hide the splash screen only when BOTH conditions are met:
   * - The initial navigation has completed (route resolved, lazy chunks loaded)
   * - The first render paint is done (web components registered, DOM stable)
   */
  private tryUnlock(): void {
    if (this.firstNavigationDone && this.viewReady) {
      this.loader.set(false);
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData['animation'] || 'items';
  }

  protected handleSideNavShow() {
    this.isSideNavOpen.set(true);
  }

  protected handleSideNavHide() {
    this.isSideNavOpen.set(false);
  }

  @HostListener('window:resize')
  protected onWindowResize() {
    this.applyResponsiveLayout();
  }

  private connectHeaderSideNav() {
    const header = this.header();
    const sideNav = this.sideNav();
    if (header && sideNav) {
      header.nativeElement.connectSideNavigation(sideNav.nativeElement);
    }
  }

  private applyResponsiveLayout() {
    const sideNav = this.sideNav();
    if (!sideNav) return;

    const isDesktop = window.innerWidth >= this.desktopBreakpoint;

    if (!isDesktop && this.isSideNavOpen()) {
      this.autoCollapsedByResize = true;
      sideNav.hide?.();
      this.isSideNavOpen.set(false);
      return;
    }

    if (isDesktop && this.autoCollapsedByResize && !this.isSideNavOpen()) {
      this.autoCollapsedByResize = false;
      sideNav.show?.();
      this.isSideNavOpen.set(false);
    }
  }
}
